<template>
  <PageLayout class="page">
    <template slot="header">
      <div class="d-flex m-3 mx-lg-0">
        <Breadcrumb :list="breadcrumbList" />
        <div class="header-actions" v-if="!emptyState">
          <b-button
            v-if="!$can('access', 'read-only')"
            variant="tertiary"
            @click="$refs.groupsImporterModal.show()"
            class="mr-2 mb-2"
            :title="labels.import"
            :disabled="!$can('create', 'group') || isBasicPlan"
          >
            <i class="material-icons">
              file_upload
            </i>
            <span class="ml-2">
              {{ labels.import }}
            </span>
          </b-button>
          <b-button
            v-if="!$can('access', 'read-only')"
            variant="tertiary"
            v-b-modal.modalGroups
            class="mb-2"
            :title="labels.newGroup"
            :disabled="!$can('create', 'group')"
          >
            <i class="mdi mdi-account-multiple-plus"></i>
            <span class="pl-1">
              {{ labels.newGroup }}
            </span>
          </b-button>
        </div>
        <GroupsTreeSelect
          v-if="emptyState"
          class="ml-md-1"
          @selectItem="selectGroupItem"
        />
      </div>
      <MainNavbar />
    </template>
    <template slot="main">
      <b-card class="main">
        <!-- Empty state -->
        <div class="empty-state-box" v-if="!emptyState">
          <video muted loop autoplay src="home/empty-state-groups.mp4" />
          <span>{{ labels.emptyStateMessage }}</span>
        </div>
        <!-- END - Empty state -->

        <!-- State -->
        <DashboardGroup
          v-if="emptyState"
          :currentGroup="currentGroup"
          :groups="groups"
          @breadcrumbClick="onBreadcrumbClick"
          @groupEntryClick="onGroupEntryClick"
          @groupEditClick="editGroup"
          @groupRemoveClick="onGroupRemoveClick"
          @openGroupsImporterModal="$refs.groupsImporterModal.show()"
        />
        <!-- END - State -->
      </b-card>
      <GroupsImporter ref="groupsImporterModal" @reloadGroups="reloadGroups" />
      <div v-watch-menu-state>
        <b-modal
          id="modalGroups"
          :title="group._id ? labels.editGroup : labels.newGroup"
          :ok-title="labels.create"
          @hidden="onModalHidden"
          ref="modalGroups"
          hide-footer
          header-bg-variant="light"
          no-close-on-backdrop
        >
          <FormGroup
            :group="group"
            @saveGroup="saveGroup"
            :groupErrors="errors"
            :submitInProgress="modal.submitInProgress"
          >
          </FormGroup>
        </b-modal>
      </div>
      <div v-watch-menu-state>
        <b-modal
          id="modalDeleteGroups"
          :title="`${labels.modalDelete.header} '${groupToRemove.name}'`"
          :cancel-title="resources.general.buttons.cancel"
          :ok-title="resources.general.buttons.delete"
          cancel-variant="outline-primary"
          @hidden="onModalHidden"
          @ok="onConfirmRemove"
          ref="modalDeleteGroups"
          header-bg-variant="light"
          no-close-on-backdrop
        >
          <p class="txt-20">{{ labels.modalDelete.title }}</p>
          <small>{{ labels.modalDelete.description }}</small>
        </b-modal>
      </div>
      <div v-watch-menu-state>
        <b-modal
          id="modalInactiveGroups"
          :title="labels.modalInactiveUsers.header"
          :cancel-title="resources.general.buttons.cancel"
          :ok-title="labels.modalInactiveUsers.ok"
          cancel-variant="outline-primary"
          @hidden="onModalHidden"
          @ok="onConfirmInactiveUsers"
          ref="modalInactiveGroups"
          header-bg-variant="light"
          no-close-on-backdrop
        >
          <p>
            {{ labels.modalInactiveUsers.text1 }}
            <span class="txt-20">{{ groupInactiveUsers.name }}</span>
            .
          </p>
          <p>{{ labels.modalInactiveUsers.text2 }}</p>
          <small>{{ labels.modalInactiveUsers.text3 }}</small>
        </b-modal>
      </div>
    </template>
  </PageLayout>
</template>

<script>
import { mapGetters } from 'vuex'
import PageLayout from '@/components/Home/PageLayout.vue'
import FormGroup from './Groups/FormGroup.vue'
import DashboardGroup from './Groups/DashboardGroup.vue'
import ability from '@/helpers/ability'
import Breadcrumb from '@/components/Shared/Breadcrumb.vue'
import { getUser, isBasicPlan } from '@/service/user'
import GroupsTreeSelect from '@/components/Groups/GroupsTreeSelect.vue'
import GroupsImporter from '@/components/Groups/GroupsImporter.vue'
import MainNavbar from '@/components/Management/MainNavbar'

export default {
  components: {
    PageLayout,
    FormGroup,
    DashboardGroup,
    Breadcrumb,
    GroupsTreeSelect,
    GroupsImporter,
    MainNavbar,
  },
  mounted: function() {
    this.$root.$emit('setPageTitle', { context: this, path: 'groups' })
    this.$ability.update(ability().rules)
  },
  data() {
    return {
      togglePopover: true,
      emptyState: true,
      groups: [],
      group: {},
      errors: {},
      groupToRemove: {},
      groupInactiveUsers: {},
    }
  },
  computed: {
    ...mapGetters({
      resources: 'resources',
      modal: 'groups/modal',
      selectedGroup: 'user/selectedGroup',
    }),

    groupList() {
      return this.$store.getters['groups/groupList']
    },

    currentGroup() {
      return this.$store.getters['groups/currentGroup']
    },

    labels() {
      return this.resources.page.home.groups
    },

    breadcrumbList() {
      return [
        {
          label: this.resources.general.menu.settings,
        },
      ]
    },

    user() {
      return getUser()
    },

    isBasicPlan() {
      return isBasicPlan()
    },
  },
  watch: {
    groupList(list) {
      this.groups = list
      this.emptyState = !(!this.groups.length && !this.currentGroup._id)
    },
    'modal.data'(group) {
      group.managers.map(item => {
        item.id = item._id
        return item
      })
      group.members.map(item => {
        item.id = item._id
        return item
      })
      this.group = Object.assign({}, group)
      if (group.managers !== undefined) {
        this.group.managers.map(i => Object.assign(i, { label: i.id }))
      }
      if (group.members !== undefined) {
        this.group.members.map(i => Object.assign(i, { label: i.id }))
      }
    },
  },
  methods: {
    reloadGroups() {
      this.$store.dispatch('user/loadGroupsTree')
      this.$store.dispatch('groups/resetPage')
    },

    selectGroupItem(group) {
      const groupId = group._id === 'company' ? null : group._id
      this.$router.push({ name: 'dashboard.groups', params: { groupId } })
    },

    saveGroup($event) {
      this.$store.dispatch('groups/setSubmitProgress', true)
      return this.$store
        .dispatch('groups/saveGroup', $event)
        .then(this.onSaveSuccess)
        .catch(this.onSaveError)
    },

    onSaveSuccess({ data }) {
      this.$root.$emit('track:app', {
        event: 'Criou grupos',
      })

      // load groups tree
      this.$store.dispatch('user/loadGroupsTree')

      this.loadPage(this.currentGroup._id)
      this.$refs.modalGroups.hide()
      this.$store.dispatch('groups/setSubmitProgress', false)

      if (data.inactive) {
        this.groupInactiveUsers = data
        this.$refs.modalInactiveGroups.show()
        this.$store.dispatch('groups/setInactiveUsers', data)
      }
    },

    onSaveError(error) {
      this.errors = JSON.parse(JSON.stringify((error.response || {}).data))
      this.$store.dispatch('groups/setSubmitProgress', false)
    },

    onGroupEntryClick(group) {
      this.$router.push({
        name: 'dashboard.groups',
        params: { groupId: group._id },
      })
    },

    onGroupRemoveClick(group) {
      this.groupToRemove = group
      this.$refs.modalDeleteGroups.show()
      this.$store.dispatch('groups/setGroupToRemove', group)
    },

    onConfirmInactiveUsers() {
      this.$store.dispatch('groups/setSubmitProgress', true)
      this.$store
        .dispatch('groups/usersInactive')
        .then(() => {
          this.loadPage(this.currentGroup._id)
          this.$store.dispatch('groups/setSubmitProgress', false)
          this.$refs.modalInactiveGroups.hide()
        })
        .catch(this.onSaveError) //@ TODO mudar method
    },

    onConfirmRemove() {
      this.$store
        .dispatch('groups/removeGroup')
        .then(() => {
          this.loadPage(this.currentGroup._id)
        })
        .catch(err => {
          this.$root.$emit('error', err)
        })
    },

    onBreadcrumbClick(group) {
      if (!group) {
        this.$store.dispatch('user/updateSelectedGroup', null)
        return this.$router.push({ name: 'dashboard.groups' })
      }
      this.$router.push({
        name: 'dashboard.groups',
        params: { groupId: group._id },
      })
    },

    onModalHidden() {
      this.$store.dispatch('groups/resetModal')
    },

    editGroup(group) {
      this.$refs.modalGroups.show()
      this.$store.dispatch('groups/setGroupToEdit', group)
    },

    loadPage(groupId = undefined) {
      if (groupId) {
        this.$store.dispatch('user/updateSelectedGroup', groupId)
        this.$store.dispatch('groups/setCurrentGroup', groupId)
      } else if (this.selectedGroup) {
        this.$router.push({
          name: 'dashboard.groups',
          params: { groupId: this.selectedGroup },
        })
      } else {
        this.$store.dispatch('groups/resetPage')
      }
    },
  },

  beforeMount() {
    this.loadPage(this.$route.params.groupId)
  },

  beforeRouteUpdate(to, from, next) {
    this.loadPage(to.params.groupId)
    next()
  },
}
</script>

<style lang="scss" scoped>
@import '~@/styles/custom/colors';
.text-bold {
  font-weight: 500;
}
.txt-20 {
  font-size: 20px;
}

.page {
  .header {
    align-items: center;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;

    .header-actions {
      .btn {
        width: 175px !important;
      }
    }
  }

  .main {
    border-radius: 8px;
    margin-bottom: 150px;

    .empty-state-box {
      overflow-y: hidden;
      padding: 15px;
      display: flex;
      flex-direction: column;
      align-items: center;
      max-height: calc(100vh - 350px);
      text-align: center;
      width: 100%;

      video {
        text-align: center;
        min-height: 130px;
        max-height: 500px;
        max-width: 100%;
      }

      span {
        margin-top: 1rem;
        font-size: 1.7rem;
        font-weight: 500;
        color: map-get($theme-colors, 'gray03');
      }
    }
  }
}
</style>
<style lang="scss">
.fix-175 .modal-dialog {
  @media (min-width: 992px) {
    transform: translateX(175px) !important;
  }
}

.fix-33 .modal-dialog {
  transform: translateX(33px) !important;
}
</style>
